import React from 'react';

import { has } from 'lodash';
import { DateTime } from 'luxon';
import { FormattedMessage } from 'react-intl';

import { PurchaseOrderTableItem } from '@modules/advancement/purchaseOrder/duck/selectorsTyped';
import {
  ItemsPerSubUnit,
  PendingItem,
  TableClickActions,
} from '@modules/advancement/unitAdvancement/types';
import { isUserInSubUnitChecker } from '@modules/advancement/utilsTyped';
import { getPreferredName } from '@modules/shared/utils/preferredName';
import {
  DateView,
  PersonAvatar,
  S,
  TableHeadingSort,
} from '@shared/components';
import { intl } from '@shared/localization';
import { tableSortOrder } from '@utils';

import PendingItemAdvancementName from '../PendingItemAdvancementName';
import PendingItemType from '../PendingItemType';
import styles from './pendingItemsTableColumns.less';

const columns = ({
  sorter,
  onGoToYouthProfile,
  onGoToAdultProfile,
  onGoToPODetails,
}: {
  sorter: {
    columnKey: string;
    order: string;
  };
  onGoToYouthProfile: TableClickActions['onGoToYouthProfile'];
  onGoToAdultProfile: TableClickActions['onGoToAdultProfile'];
  onGoToPODetails?: (poid: string) => void;
}) => {
  const nameSortOrder = tableSortOrder(sorter, 'name');
  const advancementItemSortOrder = tableSortOrder(sorter, 'advancementItem');
  const completionDateSortOrder = tableSortOrder(sorter, 'completionDate');

  type TableRow = PendingItem | ItemsPerSubUnit | PurchaseOrderTableItem;

  const isHeader = (row: TableRow): row is ItemsPerSubUnit =>
    has(row, 'children');

  return [
    {
      key: 'subUnitName',
      width: '1rem',
      render(_: unknown, item: PendingItem) {
        if (isHeader(item))
          return {
            children: (
              <div className="header-indent-wrap">{item.subUnitName}</div>
            ),
            props: {
              colSpan: 8,
            },
          };
        return null;
      },
    },
    {
      key: 'avatar',
      width: '5.5rem',
      align: 'right',
      render(_: unknown, item: PendingItem) {
        if (isHeader(item)) return { props: { colSpan: 0 } };

        return {
          children: (
            <div className="no-indent">
              <PersonAvatar
                className="no-indent"
                isAdult={item.isAdult}
                src={item.profilePictureUrl}
              />
            </div>
          ),
        };
      },
    },
    {
      key: 'name',
      width: '20rem',
      sorter: (a: PurchaseOrderTableItem, b: PurchaseOrderTableItem) =>
        a.name?.localeCompare(b.name || ''),
      sortOrder: nameSortOrder,
      title: (
        <TableHeadingSort name="name" sorter={sorter}>
          <FormattedMessage id="advancement.PendingItems.column.name" />
        </TableHeadingSort>
      ),
      render(_: unknown, data: PendingItem) {
        if (isHeader(data)) return { props: { colSpan: 0 } };

        const {
          userId,
          isAdult,
          activityTypeId,
          firstName,
          lastName,
          nickName,
        } = data;
        const personPreferredName = getPreferredName({
          firstName,
          lastName,
          nickName,
        });
        let onClickName = undefined;
        if (isAdult) {
          onClickName = onGoToAdultProfile(userId, activityTypeId);
        } else {
          onClickName = onGoToYouthProfile(userId, activityTypeId);
        }

        if (!isUserInSubUnitChecker(data)) {
          return (
            <S size="4" bold inlineBlock>
              {personPreferredName}
            </S>
          );
        }

        return (
          <S
            size="4"
            bold
            inline
            colored
            title={personPreferredName}
            className={styles.youthName}
            onClick={onClickName}
          >
            {personPreferredName}
          </S>
        );
      },
    },
    {
      dataIndex: 'memberId',
      title: intl.formatMessage({
        id: 'advancement.PendingItems.column.memberId',
      }),
      width: '10rem',
      render(_memberId: unknown, row: PendingItem) {
        if (isHeader(row)) return { props: { colSpan: 0 } };
        return _memberId;
      },
    },
    {
      key: 'advancementType',
      title: intl.formatMessage({
        id: 'advancement.PendingItems.column.itemType',
      }),
      render(_: unknown, row: PendingItem) {
        if (isHeader(row)) return { props: { colSpan: 0 } };
        return <PendingItemType item={row} />;
      },
    },
    {
      key: 'advancementItem',
      title: (
        <TableHeadingSort name="advancementItem" sorter={sorter}>
          <FormattedMessage id="advancement.PendingItems.column.itemDescription" />
        </TableHeadingSort>
      ),
      sorter: (a: PurchaseOrderTableItem, b: PurchaseOrderTableItem) => {
        if (a.advancementName == null && b.advancementName == null) return 0;
        if (a.advancementName == null) return -1;
        if (b.advancementName == null) return 1;

        return a.advancementName.localeCompare(b.advancementName);
      },
      sortOrder: advancementItemSortOrder,
      render(_: unknown, row: PendingItem) {
        if (isHeader(row)) return { props: { colSpan: 0 } };
        return (
          <PendingItemAdvancementName type={row.type} item={row} showIcon />
        );
      },
    },
    {
      key: 'completionDate',
      dataIndex: 'date',
      title: (
        <TableHeadingSort name="completionDate" sorter={sorter}>
          <FormattedMessage id="advancement.PendingItems.column.itemCompletionDate" />
        </TableHeadingSort>
      ),
      sorter: (a: PendingItem, b: PendingItem) => {
        const dateA = DateTime.fromISO(a.date);
        const dateB = DateTime.fromISO(b.date);
        return dateA.toMillis() - dateB.toMillis();
      },
      sortOrder: completionDateSortOrder,
      render(_date: string, row: PendingItem) {
        if (isHeader(row)) return { props: { colSpan: 0 } };
        return <DateView value={_date} />;
      },
    },
    {
      key: 'poid',
      title: intl.formatMessage({
        id: 'advancement.PendingItems.column.poid',
      }),
      render(_: unknown, row: PendingItem) {
        if (isHeader(row)) return { props: { colSpan: 0 } };
        return (
          <S
            size="4"
            bold
            inline
            colored
            className={styles.poid}
            onClick={() =>
              onGoToPODetails && row.poid ? onGoToPODetails(row.poid) : {}
            }
          >
            {row.poid}
          </S>
        );
      },
    },
  ];
};

export default columns;
